import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";

interface SplitPaneProps {
  /** Storage key — separate ratios per page. */
  storageKey: string;
  /** Default fraction of the *first* child. */
  defaultRatio?: number;
  /** Min fraction for either side. */
  minRatio?: number;
  maxRatio?: number;
  children: [ReactNode, ReactNode];
}

/**
 * Vertical (top/bottom) split pane with a draggable handle.
 * The ratio is persisted to localStorage so revisits feel sticky.
 */
export function SplitPane({
  storageKey,
  defaultRatio = 0.55,
  minRatio = 0.2,
  maxRatio = 0.85,
  children,
}: SplitPaneProps) {
  const wrapRef = useRef<HTMLDivElement>(null);
  const [ratio, setRatio] = useState(() => {
    try {
      const v = localStorage.getItem(storageKey);
      if (v) {
        const n = Number(v);
        if (Number.isFinite(n) && n >= minRatio && n <= maxRatio) return n;
      }
    } catch {
      /* ignore */
    }
    return defaultRatio;
  });
  const [dragging, setDragging] = useState(false);

  useEffect(() => {
    try {
      localStorage.setItem(storageKey, String(ratio));
    } catch {
      /* ignore */
    }
  }, [ratio, storageKey]);

  const onMouseMove = useCallback(
    (e: MouseEvent) => {
      const wrap = wrapRef.current;
      if (!wrap) return;
      const rect = wrap.getBoundingClientRect();
      if (rect.height <= 0) return;
      let next = (e.clientY - rect.top) / rect.height;
      next = Math.max(minRatio, Math.min(maxRatio, next));
      setRatio(next);
    },
    [minRatio, maxRatio],
  );

  useEffect(() => {
    if (!dragging) return;
    const onUp = () => setDragging(false);
    document.body.style.cursor = "row-resize";
    document.body.style.userSelect = "none";
    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onUp);
    return () => {
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onUp);
    };
  }, [dragging, onMouseMove]);

  return (
    <div
      ref={wrapRef}
      className="split"
      style={{
        gridTemplateRows: `${(ratio * 100).toFixed(2)}% 6px 1fr`,
      }}
    >
      <div className="split__pane split__pane--top">{children[0]}</div>
      <div
        className={`split__handle${dragging ? " is-dragging" : ""}`}
        role="separator"
        aria-orientation="horizontal"
        aria-valuenow={Math.round(ratio * 100)}
        onMouseDown={(e) => {
          e.preventDefault();
          setDragging(true);
        }}
      />
      <div className="split__pane split__pane--bot">{children[1]}</div>
    </div>
  );
}
